/**
 * Admin — poll moderation via the shared ModerationScreen pattern: scope +
 * active/closed status, results jump, close-poll PATCH action (optimistic,
 * reverting on error). Wired: GET/PATCH /api/admin/polls.
 * Port of screens-admin.jsx AdminPolls.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, num, str, useGateList } from '@/components/gate/api';
import { ModerationScreen, SmallButton } from '@/features/account-admin/bits';
import { ScopeBadge } from '@/features/community/bits';
import { api } from '@/lib/client';
import { AppText, Card, Pill } from '@/ui';

type Row = { id: string; q: string; scope: string; votes: number; deadline: string; closed: boolean };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** GET /api/admin/polls sends `deadline` as a raw ISO timestamp (with
 * microseconds + UTC offset) — humanize it the same way the resident-facing
 * poll list does, instead of showing the raw string verbatim. */
function humanizeDeadline(raw: string): string {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw; // already human text — show as-is
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days <= 0) return 'Closes today';
  if (days === 1) return 'Closes tomorrow';
  if (days < 30) return `Closes in ${days} days`;
  return `Closes ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function toRow(p: unknown, i: number): Row {
  const status = (str(p, 'status') ?? '').toLowerCase();
  return {
    id: str(p, 'id') ?? `p${i}`,
    q: str(p, 'q', 'question', 'title') ?? 'Poll',
    scope: str(p, 'scope', 'audience') ?? 'All wings',
    votes: num(p, 'votes', 'voteCount', 'totalVotes') ?? 0,
    deadline: humanizeDeadline(str(p, 'deadline', 'deadlineLabel') ?? ''),
    closed: status === 'closed' || (bool(p, 'closed', 'isClosed') ?? false),
  };
}

export default function AdminPollsScreen() {
  const { items, setItems, state } = useGateList<unknown>('/api/admin/polls', 'polls');
  const rows = items.map(toRow);
  const [notice, setNotice] = React.useState<string | null>(null);

  const closePoll = (id: string) => {
    const prev = items;
    setNotice(null);
    setItems(prev.map((p, i) => (toRow(p, i).id === id
      ? { ...(p as object), status: 'closed', closed: true, isClosed: true }
      : p))); // optimistic
    api.patch<unknown>(`/api/admin/polls/${id}`, { status: 'closed' }).catch(() => {
      setItems(prev); // revert
      setNotice('Could not close the poll. Try again.');
    });
  };

  return (
    <ModerationScreen
      title="Polls"
      createLabel="Create poll"
      onCreate={() => router.push('/admin/create-poll')}
      state={state}
      count={rows.length}
      emptyIcon="list"
      emptyTitle="No polls yet"
      emptyBody="Ask residents a question. Tap + to create the first poll."
      notice={notice}
    >
      {rows.map((p) => (
        <Card key={p.id}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.s2 }}>
            <ScopeBadge scope={p.scope} />
            {p.closed ? <Pill status="pending" label="CLOSED" /> : <Pill status="open" label="ACTIVE" />}
          </View>
          <AppText variant="bodyMd" weight="500" style={{ marginTop: space.s2 }}>{p.q}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s1 }}>
            {[`${p.votes} votes`, p.deadline].filter(Boolean).join(' · ')}
          </AppText>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s3 }}>
            <SmallButton label="Results" onPress={() => router.push(`/polls/${p.id}`)} />
            <View style={{ flex: 1 }} />
            {!p.closed ? (
              <SmallButton label="Close poll" danger onPress={() => closePoll(p.id)} />
            ) : null}
          </View>
        </Card>
      ))}
    </ModerationScreen>
  );
}
